import { Drawer, Loader, Select } from "@mantine/core";
import React, { useState, useMemo, useCallback, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useReducedMotion } from "@mantine/hooks";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import { formatOrgName } from "utils/insights";
import css from "./ExternalOrgTimeCard.module.scss";

const colors = [
  { color: "#9dc9e8", tint: "#e8f3fd", ink: "#175f8f" },
  { color: "#e8cc91", tint: "#fff1d6", ink: "#8a5a00" },
  { color: "#a5d2b8", tint: "#e9f7ef", ink: "#1d7a4a" },
  { color: "#e5b0b0", tint: "#fde8e8", ink: "#a12c2c" },
  { color: "#c0afe3", tint: "#efeafd", ink: "#5b3fb8" },
  { color: "#b5d7ed", tint: "#e8f3fd", ink: "#175f8f" },
  { color: "#efd9ac", tint: "#fff1d6", ink: "#8a5a00" },
  { color: "#bddfc9", tint: "#e9f7ef", ink: "#1d7a4a" },
];
const hoursLabel = (hours) => `${hours.toFixed(1)}h`;

const ExternalOrgTimeCard = ({
  data,
  isLoading,
  isError,
  source = "myOrg",
  duration,
  self,
  uid,
}) => {
  const { openDrilldown } = useInsightsDrilldown();
  const [opened, setOpened] = useState(false);
  const [sort, setSort] = useState("hours");
  const reducedMotion = useReducedMotion();
  const rows = useMemo(
    () =>
      Object.entries(data || {})
        .filter(([, info]) => info && Number.isFinite(info.hours))
        .map(([organization, info]) => ({
          organization,
          hours: Math.max(0, info.hours),
          meetings: info.meetings || 0,
        }))
        .sort(
          (a, b) =>
            b.hours - a.hours || a.organization.localeCompare(b.organization),
        )
        .map((row, i) => ({ ...row, ...colors[i % colors.length] })),
    [data],
  );
  const total = rows.reduce((sum, row) => sum + row.hours, 0);
  const others = rows.slice(5);
  const othersHours = others.reduce((sum, row) => sum + row.hours, 0);
  const maxHours = Math.max(rows[0]?.hours || 0, othersHours, 1);
  const canDrill = source === "myOrg" && Boolean(uid && duration && self);
  const openOrganization = useCallback(
    (organization) => {
      if (!canDrill) return;
      setOpened(false);
      openDrilldown({
        name: `${formatOrgName(organization)} Meetings`,
        type: "External Organization",
        orgName: organization,
        redisKey: `users:${uid}:orgCalculations:externalOrgsTime:${duration}:${self}:${organization}`,
      });
    },
    [canDrill, openDrilldown, uid, duration, self],
  );
  const clickRef = useRef(openOrganization);
  clickRef.current = openOrganization;
  const events = useMemo(
    () => ({ click: (params) => clickRef.current(params.name) }),
    [],
  );
  const option = useMemo(
    () => ({
      animation: !reducedMotion,
      animationDurationUpdate: 550,
      tooltip: {
        trigger: "item",
        renderMode: "richText",
        formatter: (p) =>
          `${formatOrgName(p.name)}\n${hoursLabel(p.value)} · ${p.percent}%`,
      },
      series: [
        {
          id: "organizations",
          type: "pie",
          radius: ["69%", "86%"],
          center: ["50%", "50%"],
          label: { show: false },
          labelLine: { show: false },
          showZeroSum: false,
          itemStyle: { borderWidth: 2, borderColor: "#fff", borderRadius: 3 },
          emphasis: { scaleSize: 5 },
          cursor: canDrill ? "pointer" : "default",
          data: rows.map((r) => ({
            name: r.organization,
            value: r.hours,
            itemStyle: { color: r.color },
          })),
        },
      ],
    }),
    [rows, reducedMotion, canDrill],
  );
  const sortedRows = [...rows].sort((a, b) => b[sort] - a[sort]);

  const compactRow = (row, isOthers = false) => {
    const name = isOthers
      ? `${others.length} others`
      : formatOrgName(row.organization);
    return (
      <button
        key={row.organization}
        className={css.row}
        disabled={!isOthers && !canDrill}
        onClick={() =>
          isOthers ? setOpened(true) : openOrganization(row.organization)
        }
        aria-label={
          isOthers
            ? `View all ${rows.length} external organizations`
            : `View ${name} meetings`
        }
      >
        <span
          className={css.avatar}
          style={
            isOthers
              ? undefined
              : { color: row.ink, backgroundColor: row.tint }
          }
        >
          {isOthers ? `+${others.length}` : name.charAt(0).toUpperCase()}
        </span>
        <span className={css.name} title={name}>
          {name}
        </span>
        <span className={css.track}>
          <span
            style={{
              width: `${(row.hours / maxHours) * 100}%`,
              background:
                !isOthers && row === rows[0] ? "#34a3ed" : "#a8d7f5",
            }}
          />
        </span>
        <span className={css.value}>{hoursLabel(row.hours)}</span>
      </button>
    );
  };

  return (
    <section className={css.card} aria-label="External organizations">
      <header className={css.header}>
        <h3>External organizations</h3>
        {!isLoading && !isError && rows.length > 0 && (
          <span>
            {hoursLabel(total)} with {rows.length}{" "}
            {rows.length === 1 ? "organization" : "organizations"}
          </span>
        )}
      </header>
      {isLoading ? (
        <div className={css.state}>
          <Loader size="sm" />
          <span>Loading external organizations</span>
        </div>
      ) : isError ? (
        <div className={css.state} role="alert">
          Unable to load external organizations.
        </div>
      ) : !rows.length ? (
        <div className={css.state}>No external meetings in this period.</div>
      ) : (
        <div className={css.rows}>
          {rows.slice(0, 5).map((row) => compactRow(row))}
          {others.length > 0 &&
            compactRow(
              { organization: "__others", hours: othersHours },
              true,
            )}
        </div>
      )}
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        size="min(960px, 100vw)"
        padding="xl"
        title="External organizations"
        classNames={{ title: css.sheetTitle, drawer: css.drawer }}
      >
        <div className={css.sheetSubtitle}>
          Time shared with external organizations · {rows.length} organizations
        </div>
        <div className={css.sheetLayout}>
          <div className={css.chartColumn}>
            <div className={css.ring}>
              <ReactECharts
                className={css.ringChart}
                option={option}
                onEvents={events}
                opts={{ renderer: "svg" }}
                style={{ height: "100%", width: "100%" }}
              />
              <div className={css.ringLabel}>
                <strong>{hoursLabel(total)}</strong>
                <span>Attributed meeting time</span>
              </div>
            </div>
            <p>
              A meeting with several organizations contributes time to each one.
            </p>
          </div>
          <div className={css.listColumn}>
            <div className={css.listHeader}>
              <span>Organization</span>
              <Select
                aria-label="Sort organizations"
                className={css.sortSelect}
                size="xs"
                value={sort}
                onChange={(value) => value && setSort(value)}
                data={[
                  { value: "hours", label: "Most time" },
                  { value: "meetings", label: "Most meetings" },
                ]}
              />
            </div>
            <div
              className={css.scrollList}
              tabIndex={0}
              role="region"
              aria-label="All external organizations"
            >
              {sortedRows.map((row) => (
                <button
                  className={css.detailRow}
                  key={row.organization}
                  disabled={!canDrill}
                  onClick={() => openOrganization(row.organization)}
                >
                  <span
                    className={css.swatch}
                    style={{ background: row.color }}
                  />
                  <span className={css.detailName}>
                    <strong>{formatOrgName(row.organization)}</strong>
                    <small>
                      {row.meetings}{" "}
                      {row.meetings === 1 ? "meeting" : "meetings"}
                    </small>
                  </span>
                  <span className={css.detailValue}>
                    <strong>{hoursLabel(row.hours)}</strong>
                    <small>
                      {total ? ((row.hours / total) * 100).toFixed(1) : "0"}% of
                      time
                    </small>
                  </span>
                  <span aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Drawer>
    </section>
  );
};
export default ExternalOrgTimeCard;
